import { useCallback, useEffect, useState } from "react";
import { Download, Loader2, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { api, formatApiError } from "../../lib/api";

const STATUSES = [["nuevo", "Nuevo"], ["contactado", "Contactado"], ["en_proceso", "En proceso"], ["incorporado", "Incorporado"], ["descartado", "Descartado"]];
const yn = (v) => (v === "si" ? "Sí" : "No");

export const LeadsPanel = () => {
  const [q, setQ] = useState("");
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async (query) => {
    try {
      const { data } = await api.get("/admin/leads", { params: { q: query } });
      setLeads(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => load(q), 300);
    return () => clearTimeout(t);
  }, [q, load]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const { data } = await api.get("/admin/leads/export", { params: { q }, responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `registros-happy-people-factory-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setExporting(false);
    }
  };

  const setStatus = async (lead, status) => {
    setLeads((l) => l.map((x) => (x.id === lead.id ? { ...x, status } : x)));
    try {
      await api.patch(`/admin/leads/${lead.id}`, { status });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (lead) => {
    if (!window.confirm(`¿Eliminar el registro de ${lead.first_name} ${lead.last_name}?`)) return;
    try {
      await api.delete(`/admin/leads/${lead.id}`);
      setLeads((l) => l.filter((x) => x.id !== lead.id));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="leads-panel">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="font-display text-3xl font-semibold tracking-tight">Registros</h2>
          <p className="mt-1 text-sm text-ink/70">Personas que completaron el formulario “Quiero conocer la oportunidad”.</p>
        </div>
        <button onClick={exportCsv} disabled={exporting} className="btn-ink !px-6 !py-3 !text-sm disabled:opacity-60" data-testid="admin-export-csv-btn">
          {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Exportar CSV
        </button>
      </div>
      <div className="relative mt-6 max-w-md">
        <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/50" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre, correo, país, ciudad o referido" className="h-11 bg-white pl-10" data-testid="leads-search-input" />
      </div>
      <div className="mt-5 overflow-x-auto rounded-2xl border border-ink/10 bg-white">
        <table className="w-full min-w-[1100px] text-left text-sm" data-testid="leads-table">
          <thead className="border-b border-ink/10 bg-paper/70 text-xs uppercase tracking-[0.06em] text-ink/70">
            <tr>
              {["Fecha", "Nombre", "Ubicación", "WhatsApp", "Correo", "Ventas", "Network M.", "Contactos", "Referido", "Estado", ""].map((h) => (
                <th key={h} className="px-4 py-3 font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={11} className="px-4 py-8 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></td></tr>
            )}
            {!loading && !leads.length && (
              <tr><td colSpan={11} className="px-4 py-10 text-center text-ink/70" data-testid="leads-empty">Sin registros todavía.</td></tr>
            )}
            {leads.map((l, i) => (
              <tr key={l.id} className="border-b border-ink/5 last:border-0" data-testid={`lead-row-${i}`}>
                <td className="whitespace-nowrap px-4 py-3 text-ink/70">{new Date(l.created_at).toLocaleDateString("es")}</td>
                <td className="px-4 py-3 font-semibold">{l.first_name} {l.last_name}</td>
                <td className="px-4 py-3">{[l.city, l.state, l.country].filter(Boolean).join(", ")}</td>
                <td className="whitespace-nowrap px-4 py-3">
                  <a href={`https://wa.me/${l.whatsapp.replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer" className="text-leaf-deep underline-offset-2 hover:underline">{l.whatsapp}</a>
                </td>
                <td className="px-4 py-3">{l.email}</td>
                <td className="px-4 py-3">{yn(l.sales_experience)}</td>
                <td className="px-4 py-3">{yn(l.mlm_experience)}</td>
                <td className="px-4 py-3">{yn(l.business_contacts)}</td>
                <td className="px-4 py-3 font-mono text-xs">{l.referral_code || "—"}</td>
                <td className="px-4 py-3">
                  <Select value={l.status} onValueChange={(v) => setStatus(l, v)}>
                    <SelectTrigger className="h-9 w-[140px]" data-testid={`lead-status-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STATUSES.map(([v, label]) => <SelectItem key={v} value={v}>{label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-4 py-3">
                  <button onClick={() => remove(l)} className="grid h-8 w-8 place-items-center rounded-full text-coral hover:bg-coral/10" aria-label="Eliminar" data-testid={`lead-delete-${i}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
